interface ValuePoint {
  number: string;
  icon: string;
  headline: string;
  subline: string;
  bullets: string[];
  badge: string;
  badgeColor: string;
  accentBg: string;
  accentText: string;
  iconBg: string;
  iconColor: string;
}

const valuePoints: ValuePoint[] = [
  {
    number: '01',
    icon: 'ri-flashlight-fill',
    headline: 'Priority Access',
    subline: 'This alone will sell the membership.',
    bullets: [
      'Early RSVP access 24–48 hours before everyone else',
      'First access to limited-cap events — bounce houses, brunches, trips',
      '"VIP-only" reserved spots held at every event',
    ],
    badge: '#1 Conversion Driver',
    badgeColor: 'bg-amber-100 text-amber-700',
    accentBg: 'bg-amber-50',
    accentText: 'text-amber-600',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  {
    number: '02',
    icon: 'ri-shield-check-line',
    headline: 'Skip the Friction',
    subline: "Moms don't want to pull out Venmo every time.",
    bullets: [
      'No RSVP fees on most events',
      'VIP pricing included — events feel free',
      'One membership covers it all',
    ],
    badge: 'Removes a Real Pain Point',
    badgeColor: 'bg-rose-100 text-rose-600',
    accentBg: 'bg-rose-50',
    accentText: 'text-rose-600',
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
  },
  {
    number: '03',
    icon: 'ri-star-smile-line',
    headline: 'VIP-Only Events',
    subline: 'Not more events. Better events.',
    bullets: [
      'Smaller brunches (8–12 moms)',
      'Hosted wine nights + networking nights',
      '"Off the calendar" pop-ups and surprises',
    ],
    badge: 'Creates Real Exclusivity',
    badgeColor: 'bg-pink-100 text-pink-700',
    accentBg: 'bg-pink-50',
    accentText: 'text-pink-600',
    iconBg: 'bg-pink-100',
    iconColor: 'text-pink-600',
  },
  {
    number: '04',
    icon: 'ri-percent-line',
    headline: 'Perks That Save Money',
    subline: 'Save $20 once — the membership pays for itself.',
    bullets: [
      'Discounts with local photographers, fitness studios, kids activities',
      'Priority booking with Mama Approved partners',
      'Spas, cleaning services, and seasonal deals',
    ],
    badge: 'Practical, Not Just Pretty',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    accentBg: 'bg-emerald-50',
    accentText: 'text-emerald-600',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
  {
    number: '05',
    icon: 'ri-medal-2-line',
    headline: 'The Identity Piece',
    subline: "Moms don't just join for value. They join for belonging.",
    bullets: [
      '"VIP" label inside the community',
      'Recognition posts from the group',
      'The feeling of being in the inner circle',
    ],
    badge: 'Underrated but Powerful',
    badgeColor: 'bg-violet-100 text-violet-700',
    accentBg: 'bg-violet-50',
    accentText: 'text-violet-600',
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
  },
];

export default function MembershipBenefits() {
  return (
    <section className="py-24 bg-[#fdf9f7]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-rose-50 border border-rose-100 px-4 py-2 rounded-full mb-6">
            <i className="ri-vip-crown-line text-rose-500 text-sm"></i>
            <span className="text-rose-600 text-xs font-bold tracking-widest uppercase">Why VIP Is Worth It</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
            5 Reasons VIP Feels Like a<br />
            <span className="text-rose-500">Shortcut to a Better Life</span>
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto leading-relaxed">
            If it doesn&apos;t feel like an obvious upgrade, it won&apos;t sell. Here&apos;s exactly what makes it worth it.
          </p>
        </div>

        {/* Value Points */}
        <div className="space-y-6">
          {valuePoints.map((point, i) => (
            <div
              key={point.number}
              className={`rounded-2xl border border-gray-100 bg-white p-7 flex flex-col md:flex-row gap-6 items-start`}
            >
              {/* Number + Icon */}
              <div className="flex items-center gap-4 shrink-0">
                <span className={`text-4xl font-extrabold ${point.accentText} opacity-20 leading-none w-10 text-center`}>
                  {point.number}
                </span>
                <div className={`w-12 h-12 flex items-center justify-center ${point.iconBg} rounded-xl shrink-0`}>
                  <i className={`${point.icon} ${point.iconColor} text-xl`}></i>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h3 className="text-xl font-extrabold text-gray-900">{point.headline}</h3>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${point.badgeColor}`}>
                    {point.badge}
                  </span>
                </div>
                <p className={`text-sm font-semibold ${point.accentText} mb-4`}>{point.subline}</p>
                <ul className="space-y-2">
                  {point.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5">
                      <div className={`w-4 h-4 flex items-center justify-center ${point.accentBg} rounded mt-0.5 shrink-0`}>
                        <i className={`ri-check-line ${point.iconColor} text-xs`}></i>
                      </div>
                      <span className="text-sm text-gray-600 leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Comparison callout */}
        <div className="mt-14 bg-gray-950 rounded-2xl p-8 text-center">
          <p className="text-white/50 text-xs font-bold tracking-widest uppercase mb-3">The Math</p>
          <p className="text-white text-2xl font-extrabold mb-2">
            Moms already spend $5–10 per event.
          </p>
          <p className="text-white/60 text-base leading-relaxed max-w-xl mx-auto mb-6">
            Most attend 1–2 events a month. At $12/mo, VIP covers that fee — and gets you in the door early
            before spots fill up. It&apos;s not a splurge. It&apos;s a no-brainer.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
            <span className="text-white/40 line-through">$10 event fee × 2 events = $20/mo</span>
            <i className="ri-arrow-right-line text-rose-400"></i>
            <span className="text-rose-300 font-bold">VIP = $12/mo. No fees. Better access.</span>
          </div>
        </div>
      </div>
    </section>
  );
}
